import { Injectable } from '@angular/core';
import { GraphService } from './graph.service';
import {
  AbstractControl,
  AsyncValidatorFn,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import {
  catchError,
  from,
  map,
  Observable,
  of,
  switchMap,
  timer,
} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { RisValidationResDto } from '../_dtos/risValidationResDto';

@Injectable({
  providedIn: 'root',
})
export class ValidatorService {
  constructor(
    private graphService: GraphService,
    private http: HttpClient,
  ) {}

  reviseValue(value: string): string {
    return value
      .replace(/–/g, '-')
      .replace(/ /g, '')
      .replace(/ä/g, 'ae')
      .replace(/Ä/g, 'Ae')
      .replace(/ö/g, 'oe')
      .replace(/Ö/g, 'Oe')
      .replace(/ü/g, 'ue')
      .replace(/Ü/g, 'Ue')
      .replace(/ß/g, 'ss')
      .replace(/é/g, 'e')
      .replace(/è/g, 'e')
      .replace(/ê/g, 'e')
      .replace(/à/g, 'a')
      .replace(/á/g, 'a')
      .replace(/â/g, 'a')
      .replace(/ñ/g, 'n')
      .replace(/ç/g, 'c')
      .replace(/É/g, 'E')
      .replace(/È/g, 'E')
      .replace(/Ê/g, 'E')
      .replace(/À/g, 'A')
      .replace(/Á/g, 'A')
      .replace(/Â/g, 'A')
      .replace(/Ñ/g, 'N')
      .replace(/Ç/g, 'C');
  }

  validateMailNicknameValue(
    value: string,
  ): { hasForbiddenChars?: boolean; exceedsMaxLength?: boolean } | null {
    const forbiddenChars = new RegExp(environment.forbiddenChars);
    const maxLength = 64 - 10; // -10 because of the added prefix

    const hasForbiddenChars = forbiddenChars.test(value);
    const exceedsMaxLength = value.length > maxLength;

    if (hasForbiddenChars) return { hasForbiddenChars: true };

    if (exceedsMaxLength) return { exceedsMaxLength: true };

    return null;
  }

  validateSiteNameValue(
    value: string,
  ): { hasForbiddenChars?: boolean; exceedsMaxLength?: boolean } | null {
    const forbiddenChars = new RegExp(environment.forbiddenChars);
    const maxLength = 80;

    const hasForbiddenChars = forbiddenChars.test(value);
    const exceedsMaxLength = value.length > maxLength;

    if (hasForbiddenChars) return { hasForbiddenChars: true };

    if (exceedsMaxLength) return { exceedsMaxLength: true };

    return null;
  }

  validateMailNickname(departmentShortName: string): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const value = control.value;
      const revisedValue = this.reviseValue(value);
      const invalidValue = this.validateMailNicknameValue(revisedValue);

      if (invalidValue) return of(invalidValue);

      const mailNickname = `${departmentShortName}-${revisedValue}`;
      return from(this.graphService.validateMailNickname(mailNickname)).pipe(
        map((isNicknameAvailable) =>
          isNicknameAvailable ? null : { mailNicknameUnavailable: true },
        ),
      );
    };
  }

  validateSharePointSiteName(departmentShortName: string): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const value = control.value;
      const revisedValue = this.reviseValue(value);
      const invalidValue = this.validateSiteNameValue(revisedValue);

      if (invalidValue) return of(invalidValue);

      return this.http
        .get<any>(
          `${environment.spoBaseUrl}/${departmentShortName}-${revisedValue}/_api/web/url`,
        )
        .pipe(
          map((response) => {
            return response ? { siteNameTaken: true } : null;
          }),
          catchError((error) => {
            if (error.status === 404) return of(null);

            return of({ siteNameTaken: true });
          }),
        );
    };
  }

  // Mirror of the Core API defaults Bookings__DisplayNamePrefix and Bookings__MailboxDomain
  private static readonly bookingsDisplayNamePrefix = 'BFH Bookings - ';
  private static readonly bookingsMailboxDomain = 'bfh.ch';
  private static readonly bookingsMaxNameLength = 105;
  private static readonly maxMailLocalPartLength = 64;

  // Graph derives the address from the name the business is created with (prefix and name, renamed afterwards) without
  // all characters other than [A-Za-z0-9]: umlauts and accents are dropped, not transliterated
  // ("Prüfung" → "BFHBookingsPrfung@bfh.ch")
  bookingsAddress(name: string): string {
    return `${this.bookingsLocalPart(name)}@${ValidatorService.bookingsMailboxDomain}`;
  }

  private bookingsLocalPart(name: string): string {
    const createName = `${ValidatorService.bookingsDisplayNamePrefix}${name.trim()}`;
    return createName.replace(/[^A-Za-z0-9]/g, '');
  }

  validateBookingsName(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const value: string = control.value ?? '';
      const invalidValue = this.validateBookingsNameValue(value);

      if (invalidValue) return of(invalidValue);

      // Angular unsubscribes on the next value change, so the timer debounces the Graph lookup while typing
      return timer(400).pipe(
        switchMap(() =>
          this.graphService.isUserPrincipalNameFree(this.bookingsAddress(value)),
        ),
        map((isFree) => (isFree ? null : { nameInUse: true })),
        catchError(() => of({ nameCheckFailed: true })),
      );
    };
  }

  validateBookingsNameValue(value: string): ValidationErrors | null {
    const forbiddenChars = new RegExp(environment.forbiddenChars);
    const prefixLength = this.bookingsLocalPart('').length;
    const localPart = this.bookingsLocalPart(value);

    // As before on the transliterated value, so umlauts and accents are allowed (same set as the Core API)
    if (forbiddenChars.test(this.reviseValue(value)))
      return { hasForbiddenChars: true };

    if (value.length > ValidatorService.bookingsMaxNameLength)
      return { exceedsMaxLength: true };

    if (localPart.length === prefixLength) return { noAddressCharacters: true };

    if (localPart.length > ValidatorService.maxMailLocalPartLength)
      return { addressTooLong: true };

    return null;
  }

  // Same pattern as the UpnValidation attribute of the Core API
  bfhUpnValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const upn = control.value?.userPrincipalName;

      if (!upn) return null;

      return /^[a-z]{3,5}\d{1,2}@bfh\.ch$/i.test(upn)
        ? null
        : { notBfhUser: true };
    };
  }

  notUserValidator(userPrincipalName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const upn: string | undefined = control.value?.userPrincipalName;

      return upn?.toLowerCase() === userPrincipalName.toLowerCase()
        ? { isCurrentUser: true }
        : null;
    };
  }

  // Bookings adds owners as staff members, which needs a mailbox; owners in skipUpns are not checked again
  mailboxValidator(skipUpns: () => string[] = () => []): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const user = control.value;

      if (!user?.id || !user.userPrincipalName) return of(null);

      const skip = skipUpns().map((upn) => upn.toLowerCase());
      if (skip.includes(user.userPrincipalName.toLowerCase())) return of(null);

      return this.graphService.hasMailbox(user.id).pipe(
        map((hasMailbox) => (hasMailbox ? null : { noMailbox: true })),
        catchError(() => of({ mailboxCheckFailed: true })),
      );
    };
  }

  integerValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (value === null || value === undefined || value === '') return null;

      return Number.isInteger(value) ? null : { notInteger: true };
    };
  }

  validateRISProjectName(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const value = control.value;
      const invalidValue = this.validateRISProjectNameValue(value);

      if (invalidValue) return of(invalidValue);

      return this.http
        .get<RisValidationResDto>(
          environment.risBaseUrl +
            '/projectnameexists/' +
            value +
            '?code=' +
            environment.risKey,
        )
        .pipe(
          map((response) => {
            return response.AlreadyExists ? { AlreadyExists: true } : null;
          }),
        );
    };
  }

  validateRISProjectNameValue(
    projectName: string,
  ): { hasForbiddenChars?: boolean; exceedsMaxLength?: boolean } | null {
    const forbiddenChars = new RegExp(environment.forbiddenChars);
    const maxLength = 40;

    const hasForbiddenChars = forbiddenChars.test(projectName);
    const exceedsMaxLength = projectName.length > maxLength;

    if (hasForbiddenChars) return { hasForbiddenChars: true };

    if (exceedsMaxLength) return { exceedsMaxLength: true };

    return null;
  }

  endDateValidator(startDateControl: AbstractControl): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || !startDateControl.value) return null;

      const endDate = new Date(control.value);
      const startDate = new Date(startDateControl.value);

      if (endDate <= startDate) return { endDateBeforeStart: true };

      return null;
    };
  }

  dateRangeValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const startDate = group.get('startDate')?.value;
      const endDate = group.get('endDate')?.value;

      if (startDate && endDate && new Date(endDate) <= new Date(startDate))
        return { dateRange: true };

      return null;
    };
  }

  userObjectValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value) return null;

      if (!value.userPrincipalName) return { invalidUserObject: true };

      return null;
    };
  }
}
